import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useCanAccess } from '../../hooks/useCanAccess'
import { viewApi } from '../../services/viewApi'
import { useBuildingStore } from '../../stores/buildingStore'
import { useViewStore } from '../../stores/viewStore'
import type { View } from '../../types'

export default function ViewSelector() {
  const currentSiteId = useBuildingStore((s) => s.currentSiteId)
  const currentViewId = useViewStore((s) => s.currentViewId)
  const loadView = useViewStore((s) => s.loadView)
  const setCurrentViewId = useViewStore((s) => s.setCurrentViewId)
  const captureSettings = useViewStore((s) => s.captureSettings)

  const [saveName, setSaveName] = useState('')
  const [showSave, setShowSave] = useState(false)
  const [editingViewId, setEditingViewId] = useState<string | null>(null)
  const [editName, setEditName] = useState('')
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)

  const queryClient = useQueryClient()

  const { data: views = [] } = useQuery({
    queryKey: ['views', currentSiteId],
    queryFn: () => viewApi.getViews(currentSiteId!),
    enabled: !!currentSiteId,
  })

  const invalidateViews = () =>
    queryClient.invalidateQueries({ queryKey: ['views', currentSiteId] })

  const saveMutation = useMutation({
    mutationFn: (name: string) => viewApi.createView(currentSiteId!, {
      name,
      settings: {
        ...captureSettings(),
        // TODO: capture real camera from ThreeCanvas ref
        cameraPosition: { x: 50, y: 50, z: 50 },
        cameraTarget: { x: 0, y: 15, z: 0 },
        cameraZoom: 1,
      },
    }),
    onSuccess: () => {
      invalidateViews()
      setSaveName('')
      setShowSave(false)
    },
  })

  const updateNameMutation = useMutation({
    mutationFn: ({ view, name }: { view: View; name: string }) =>
      viewApi.updateView(currentSiteId!, view.id, { ...view, name }),
    onSuccess: () => {
      invalidateViews()
      setEditingViewId(null)
      setEditName('')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (viewId: string) => viewApi.deleteView(currentSiteId!, viewId),
    onSuccess: (_, deletedId) => {
      if (currentViewId === deletedId) {
        setCurrentViewId(null)
      }
      invalidateViews()
      setConfirmDeleteId(null)
    },
  })

  const defaultMutation = useMutation({
    mutationFn: (view: View) => viewApi.updateView(currentSiteId!, view.id, {
      ...view,
      is_default: true,
    }),
    onSuccess: () => {
      invalidateViews()
    },
  })

  const handleSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const viewId = e.target.value
    if (!viewId) {
      setCurrentViewId(null)
      return
    }
    const view = views.find((v) => v.id === viewId)
    if (view) {
      setCurrentViewId(view.id)
      loadView(view.settings)
    }
  }

  const handleStartEdit = (view: View) => {
    setEditingViewId(view.id)
    setEditName(view.name)
  }

  const handleConfirmEdit = () => {
    if (!editingViewId || !editName.trim()) return
    const view = views.find((v) => v.id === editingViewId)
    if (view) {
      updateNameMutation.mutate({ view, name: editName.trim() })
    }
  }

  const canCreateView = useCanAccess('views', 'create')
  const canUpdateView = useCanAccess('views', 'update')
  const canDeleteView = useCanAccess('views', 'delete')

  if (!currentSiteId) return null

  const selectedView = views.find((v) => v.id === currentViewId)

  return (
    <div className="view-selector">
      <div className="panel-section__title">Vues</div>
      <select
        className="view-selector__select"
        value={currentViewId ?? ''}
        onChange={handleSelect}
      >
        <option value="">-- Vue courante --</option>
        {views.map((v) => (
          <option key={v.id} value={v.id}>
            {v.is_default ? '* ' : ''}{v.name}
          </option>
        ))}
      </select>

      {(canCreateView || canUpdateView || canDeleteView) && (
        <div className="view-selector__actions">
          {/* Save new view */}
          {canCreateView && (showSave ? (
            <div className="view-selector__save-form">
              <input
                type="text"
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                placeholder="Nom de la vue"
                className="view-selector__input"
              />
              <button
                className="view-selector__btn"
                onClick={() => saveName && saveMutation.mutate(saveName)}
                disabled={saveMutation.isPending}
              >
                OK
              </button>
              <button
                className="view-selector__btn view-selector__btn--cancel"
                onClick={() => setShowSave(false)}
              >
                X
              </button>
            </div>
          ) : (
            <button
              className="view-selector__btn"
              onClick={() => setShowSave(true)}
            >
              Sauvegarder vue
            </button>
          ))}

          {/* Edit name */}
          {canUpdateView && selectedView && (
            editingViewId === selectedView.id ? (
              <div className="view-selector__save-form">
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="view-selector__input"
                />
                <button
                  className="view-selector__btn"
                  onClick={handleConfirmEdit}
                  disabled={updateNameMutation.isPending}
                >
                  OK
                </button>
                <button
                  className="view-selector__btn view-selector__btn--cancel"
                  onClick={() => setEditingViewId(null)}
                >
                  X
                </button>
              </div>
            ) : (
              <button
                className="view-selector__btn"
                onClick={() => handleStartEdit(selectedView)}
              >
                Renommer
              </button>
            )
          )}

          {/* Set default */}
          {canUpdateView && currentViewId && (
            <button
              className="view-selector__btn"
              onClick={() => {
                const view = views.find((v) => v.id === currentViewId)
                if (view) defaultMutation.mutate(view)
              }}
              disabled={defaultMutation.isPending}
            >
              Definir par defaut
            </button>
          )}

          {/* Delete */}
          {canDeleteView && currentViewId && (
            confirmDeleteId === currentViewId ? (
              <div className="view-selector__save-form">
                <span className="view-selector__confirm-text">Supprimer ?</span>
                <button
                  className="view-selector__btn view-selector__btn--danger"
                  onClick={() => deleteMutation.mutate(currentViewId)}
                  disabled={deleteMutation.isPending}
                >
                  Oui
                </button>
                <button
                  className="view-selector__btn view-selector__btn--cancel"
                  onClick={() => setConfirmDeleteId(null)}
                >
                  Non
                </button>
              </div>
            ) : (
              <button
                className="view-selector__btn view-selector__btn--danger"
                onClick={() => setConfirmDeleteId(currentViewId)}
              >
                Supprimer vue
              </button>
            )
          )}
        </div>
      )}
    </div>
  )
}
